import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import classNames from 'classnames';
import { Tabs } from 'src/common/form/tabs';
import { LeftStrongIcon } from 'src/monet/icons';

import { DocumentViewer } from './document-viewer';
import { BlockEditor } from './block-editor';
import { TextEditorNewBlockMenu } from './new-block-menu';
import { TextEditorBlocksSummary } from './blocks-summary';
// import { EditorContent } from './editor-content';
import { Button } from 'src/common/button';

import { Document as DocumentInterface } from 'src/common/interfaces/document.interface';
import { DocumentBlock, DocumentBlockType, DocumentBlockContent } from 'src/common/interfaces/document_block.interface';

import { useDocumentEditorContext } from 'src/store/document-editor';
import {
  apiCreateDocumentBlock,
  apiUpdateDocumentBlock,
  apiDeleteDocumentBlock,
  apiReorderDocumentBlock,
} from 'src/utils/journeyApi';

import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useSpotlight } from 'src/common/spotlight/use-spotlight.hook';
import { useDocumentEditorSpotlights } from './use-document-editor-spotlights';

export const DocumentEditor = ({
  existingDocument,
  onBack,
  onDocumentUpdate,
}: {
  existingDocument: DocumentInterface;
  onBack: () => void;
  onDocumentUpdate: (document: DocumentInterface) => void;
}) => {
  const editorRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const [documentLoaded, setDocumentLoaded] = useState(false);

  const document = useDocumentEditorContext((state) => state.document) as DocumentInterface;
  const setDocument = useDocumentEditorContext((state) => state.setDocument);
  // const setDocumentBlocks = useDocumentEditorContext((state) => state.setDocumentBlocks);
  const deleteDocumentBlock = useDocumentEditorContext((state) => state.deleteDocumentBlock);
  const addDocumentBlock = useDocumentEditorContext((state) => state.addDocumentBlock);
  const updateDocumentBlock = useDocumentEditorContext((state) => state.updateDocumentBlock);
  const addInitialBlockTypeForBlock = useDocumentEditorContext((state) => state.addInitialBlockTypeForBlock);

  const [activeTab, setActiveTab] = useState('editor');

  const { anchorRef, anchorStyle, renderSpotlight } = useSpotlight({
    placement: 'left',
  });
  const { showEmptyDocumentEditorSpotlight, setSeenEmptyDocumentEditorSpotlight } = useDocumentEditorSpotlights();

  const onSpotlightDismiss = useCallback(() => {
    setSeenEmptyDocumentEditorSpotlight();
  }, [setSeenEmptyDocumentEditorSpotlight]);

  const updateBlock = useCallback(
    (block: DocumentBlock, content: DocumentBlockContent) => {
      apiUpdateDocumentBlock(document.id, block.id, content).then((blockResponse) => {
        updateDocumentBlock(block.id, blockResponse);
      });
      //
    },
    [document?.id, updateDocumentBlock]
  );

  const debouncedUpdateBlock = useMemo(
    () => debounce((block: DocumentBlock, content: DocumentBlockContent) => updateBlock(block, content), 500),
    [updateBlock]
  );

  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedUpdateBlock.cancel();
    };
  }, [debouncedUpdateBlock]);

  useEffect(() => {
    if (existingDocument && !documentLoaded) {
      setDocument(existingDocument);
      setDocumentLoaded(true);
    }
  }, [existingDocument, documentLoaded, setDocument]);

  useEffect(() => {
    onDocumentUpdate(document);
  }, [document]);

  const deleteBlock = useCallback(
    (block: DocumentBlock) => {
      apiDeleteDocumentBlock(document.id, block.id).then(() => {
        deleteDocumentBlock(block.id);
      });
    },
    [document?.id, deleteDocumentBlock]
  );

  const onAddEmptyBlock = useCallback(
    (initialBlockType: DocumentBlockType, attrs: any, previousBlockId: any) => {
      apiCreateDocumentBlock(document.id, previousBlockId).then((blockResponse: DocumentBlock) => {
        if (previousBlockId) {
          const insertIndex = document.blocks.findIndex((block) => block.id === previousBlockId);
          addDocumentBlock(blockResponse, insertIndex + 1);
        } else {
          addDocumentBlock(blockResponse, 0);
        }
        addInitialBlockTypeForBlock(blockResponse.id, initialBlockType, attrs);
      });
    },
    [document?.id, document?.blocks, addDocumentBlock, addInitialBlockTypeForBlock]
  );

  const onAddEmptyBlockToEnd = useCallback(
    (blockType: DocumentBlockType, attrs: any) => {
      const previousBlock = document.blocks[document.blocks.length - 1];

      return onAddEmptyBlock(blockType, attrs, previousBlock?.id);
    },
    [document?.blocks, onAddEmptyBlock]
  );

  const onDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;

      if (!destination) {
        return;
      }

      if (source.droppableId === destination.droppableId) {
        const blocks = document.blocks.slice();
        const block = blocks[source.index] as DocumentBlock;
        blocks.splice(source.index, 1);
        blocks.splice(destination.index, 0, block);

        const updatedDocument = {
          ...document,
          blocks: blocks,
        };

        const previousBlock = destination.index > 0 ? updatedDocument.blocks[destination.index - 1] : null;
        setDocument(updatedDocument);

        apiReorderDocumentBlock(document.id, block.id, previousBlock ? previousBlock.id : null).then(
          (blockResponse) => {
            updateDocumentBlock(block.id, blockResponse);
          }
        );
      } else if (source.droppableId === 'block-menu-items') {
        const { draggableId } = result;

        const previousBlock = destination.index > 0 ? document.blocks[destination.index - 1] : null;

        if (draggableId === 'heading1' || draggableId === 'heading2') {
          onAddEmptyBlock(
            'heading',
            { level: draggableId === 'heading1' ? 1 : 2 },
            previousBlock ? previousBlock.id : null
          );
        } else {
          onAddEmptyBlock(draggableId, {}, previousBlock ? previousBlock.id : null);
        }
      }
    },
    [document, setDocument, updateDocumentBlock, onAddEmptyBlock]
  );

  useEffect(() => {
    if (!initialized && document && document.blocks) {
      setInitialized(true);
      if (document.blocks.length === 0) {
        onAddEmptyBlock('heading', { level: 2 }, null);
      }
    }
  }, [document, initialized, onAddEmptyBlock]);

  if (!document) return null;

  return (
    <div className='flex flex-1 flex-grow justify-center min-h-screen max-w-full bg-bedrock-light-gray overflow-y-auto relative'>
      <div
        className={classNames('flex-col journey-text-editor max-w-[680px] w-full focus:outline-none relative', {
          flex: activeTab === 'editor',
          '!hidden': activeTab !== 'editor',
        })}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='droppable-document-editor-blocks'>
            {(provided: any, editorSnapshot: any) => {
              return (
                <div ref={provided.innerRef} {...provided.droppableProps} className='flex-1 flex flex-col space-y-4'>
                  {document.blocks.map((block: any, index: number) => {
                    return (
                      <BlockEditor
                        key={`block-${block.id}`}
                        draggableId={`block-${block.id}`}
                        index={index}
                        block={block}
                        onUpdateBlock={(content: DocumentBlockContent) => debouncedUpdateBlock(block, content)}
                        onDeleteBlock={() => deleteBlock(block)}
                      />
                    );
                  })}
                  {provided.placeholder}
                  <Droppable droppableId='block-menu-items' isDropDisabled={true}>
                    {(provided: any, snapshot: any) => {
                      return (
                        <div ref={provided.innerRef} {...provided.droppableProps} className='fixed bottom-0'>
                          <div className='fixed top-[320px]	right-6'>
                            <div ref={anchorRef} style={anchorStyle} className='flex bedrock-floating-button-ring'>
                              <TextEditorNewBlockMenu
                                editor={editorRef.current}
                                isDraggingOverToEditor={editorSnapshot.isDraggingOver}
                                onAddBlock={(blockType: DocumentBlockType, attrs: any) =>
                                  onAddEmptyBlockToEnd(blockType, attrs)
                                }
                              />
                            </div>
                          </div>
                          {provided.placeholder}
                        </div>
                      );
                    }}
                  </Droppable>
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
      </div>
      {activeTab === 'preview' && (
        <div className='max-w-[680px] w-full py-lg'>
          <DocumentViewer document={document} />
        </div>
      )}
      <div className='fixed top-6 right-6'>
        <Tabs
          theme='bedrock'
          value={activeTab}
          onChange={(e: any, newValue: string) => setActiveTab(newValue)}
          options={[
            {
              value: 'preview',
              label: <div>Preview</div>,
            },
            {
              value: 'editor',
              label: <div>Editor</div>,
            },
          ]}
        />
      </div>
      {activeTab === 'editor' && (
        <div className='fixed top-6 left-6'>
          <Button size='regular' variant='tertiary' icon={<LeftStrongIcon />} label='Back' onClick={onBack} />
          <div className='mt-6'>
            <TextEditorBlocksSummary document={document} onDragEnd={onDragEnd} />
          </div>
        </div>
      )}
      {renderSpotlight({
        content: <div>Add your first block to start populating this page</div>,
        showDismissButton: true,
        show: showEmptyDocumentEditorSpotlight(document),
        onDismiss: onSpotlightDismiss,
      })}
    </div>
  );
};
